import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link, useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

const ActiveRFQsTab = () => {
  // State for data
  const [loading, setLoading] = useState(false);
  const [activeRfqs, setActiveRfqs] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Modal state
  const [displayModal, setDisplayModal] = useState(false);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [loadingRfqDetails, setLoadingRfqDetails] = useState(false);

  const navigate = useNavigate();

  // Fetch current user data when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;
        
        // Check if the value needs to be parsed
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue; // Assume it's already an object
        }
        
        // Safely access and set user details
        if (parsedUser?.user) {
          setCurrentUser(parsedUser.user);
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log(err.message);
      }
    };
    
    getCurrentUser();
  }, []);

  // Fetch supplier RFQs when user data is available
  useEffect(() => {
    if (currentUser && currentUser.userId) {
      fetchActiveRfqs(currentUser.userId);
    }
  }, [currentUser]);

  // Function to fetch only active RFQs for the current supplier
  const fetchActiveRfqs = async (supplierId) => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/app/quotations/suppliers/${supplierId}`,
        "GET"
      );
  
      if (response.success && response.data) {
        // Check if data is an array directly or if it's within response.data.data
        const rfqData = Array.isArray(response.data) ? response.data : response.data.data;
        
        if (Array.isArray(rfqData) && rfqData.length > 0) {
          // Filter only active RFQs
          const filteredActiveRfqs = rfqData.filter(rfq => rfq.status === 'Active');
          setActiveRfqs(filteredActiveRfqs);
          
          if (filteredActiveRfqs.length === 0) {
            toastify("No active RFQs found", "info");
          }
        } else {
          setActiveRfqs([]);
          toastify("No RFQs found in the data", "info");
        }
      } else {
        setActiveRfqs([]);
        toastify("No RFQs found or error fetching data", "info");
      }
    } catch (error) {
      toastify(error.message || "Error fetching active RFQs", "error");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch RFQ details by ID
  const fetchRfqDetails = async (rfqId) => {
    try {
      setLoadingRfqDetails(true);
      const response = await makeRequest2(`/api/app/quotations/by_id/${rfqId}`, "GET");

      if (response.success && response.data.data) {
        setSelectedRfq(response.data.data);
        setDisplayModal(true);
      } else {
        toastify("Error fetching RFQ details", "error");
      }
    } catch (error) {
      toastify(error.message || "Error fetching RFQ details", "error");
    } finally {
      setLoadingRfqDetails(false);
    }
  };

  // Function to navigate to the response screen
  const navigateToResponse = (rfqId) => {
    navigate(`/facility/supplier_rfq_management/tabs/add_rfq_response/${rfqId}`);
  };

  const statusTemplate = (rowData) => {
    const statusStyles = {
      Pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
      Active: { color: '#28a745', backgroundColor: '#e9fbe9' },
      Closed: { color: '#6c757d', backgroundColor: '#f1f1f1' }
    };
    
    const style = statusStyles[rowData.status] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1'
    };
    
    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block'
        }}
      >
        {rowData.status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openingDateTemplate = (rowData) => {
    return formatDate(rowData.startDate);
  };

  const closingDateTemplate = (rowData) => {
    return formatDate(rowData.closingDate);
  };

  const rfqFeeTemplate = (rowData) => {
    if (rowData.rfqFee === 0 || rowData.rfqFee === undefined) {
      return 'Free';
    }
    
    return `${rowData.currency || ''} ${rowData.rfqFee?.toFixed(2) || 0}`;
  };

  const categoryTemplate = (rowData) => {
    return rowData.category || rowData.rfqType || 'N/A';
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2 justify-content-center">
        <Button
          icon="ti ti-eye"
          className="p-button-text p-button-rounded p-button-info"
          tooltip="View RFQ"
          onClick={() => fetchRfqDetails(rowData._id)}
        />
        {rowData.status !== 'Closed' && (
          <Button
            icon="ti ti-send"
            className="p-button-text p-button-rounded p-button-success"
            tooltip="Send Quotation"
            onClick={() => navigateToResponse(rowData._id)}
          />
        )}
      </div>
    );
  };

  // Modal footer with close button
  const modalFooter = (
    <div>
      <Button
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => setDisplayModal(false)}
      >
        Close
      </Button>
      &nbsp;
      {selectedRfq?.status !== 'Closed' && (
        <Button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setDisplayModal(false);
            if (selectedRfq) {
              navigateToResponse(selectedRfq._id);
            }
          }}
        >
          Send Quotation
        </Button>
      )}
    </div>
  );

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="mb-0">Active RFQs</h4>
      </div>
      <div className="card-body">
        <DataTable
          value={activeRfqs}
          emptyMessage="No active RFQs found."
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          stripedRows
          loading={loading}
          tableStyle={{ minWidth: '100%' }}
          responsiveLayout="scroll"
          sortMode="multiple"
        >
          <Column field="rfqNumber" header="RFQ Number" sortable />
          <Column field="name" header="RFQ Name" sortable />
          <Column header="Category" body={categoryTemplate} sortable field="category" />
          <Column header="RFQ Fee" body={rfqFeeTemplate} sortable field="rfqFee" />
          <Column header="Opening Date" body={openingDateTemplate} sortable field="startDate" />
          <Column header="Closing Date" body={closingDateTemplate} sortable field="closingDate" />
          <Column field="status" header="Status" body={statusTemplate} sortable />
          <Column header="Actions" body={actionTemplate} style={{ width: '100px' }} />
        </DataTable>
      </div>

      {/* RFQ Details Modal */}
      <Dialog
        header="RFQ Details"
        visible={displayModal}
        style={{ width: '70vw' }}
        footer={modalFooter}
        onHide={() => setDisplayModal(false)}
        closable
        maximizable
      >
        {loadingRfqDetails && (
          <div className="d-flex justify-content-center">
            <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem' }}></i>
          </div>
        )}

        {!loadingRfqDetails && selectedRfq && (
          <div className="rfq-details">
            <div className="row mb-3">
              <div className="col-md-6">
                <h5 className="mb-1">RFQ Number</h5>
                <p>{selectedRfq.rfqNumber}</p>
              </div>
              <div className="col-md-6">
                <h5 className="mb-1">RFQ Name</h5>
                <p>{selectedRfq.name}</p>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <h5 className="mb-1">Opening Date</h5>
                <p>{formatDate(selectedRfq.startDate)}</p>
              </div>
              <div className="col-md-6">
                <h5 className="mb-1">Closing Date</h5>
                <p>{formatDate(selectedRfq.closingDate)}</p>
              </div>
            </div>

            {selectedRfq.items && selectedRfq.items.length > 0 && (
              <div className="row mb-3">
                <div className="col-12">
                  <h5 className="mb-3">Items</h5>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Quantity</th>
                          <th>Unit</th>
                          <th>Specifications</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRfq.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.itemName}</td>
                            <td>{item.description || 'N/A'}</td>
                            <td>{item.quantity}</td>
                            <td>{item.unitOfMeasure}</td>
                            <td>{item.specifications || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ActiveRFQsTab;